import {
  AIRTIME_MANAGEMENT,
  CARRIER_MANAGEMENT,
  FILTER_FEATURE,
  PARTNER_MANAGEMENT,
  STORE_API,
} from "api/ApiPath";
import { BaseApiResponseModel } from "app/based/baseApiModel/baseApiResponseModel/BaseApiResponseModel";
import { ListBaseApiResponseModel } from "app/based/baseApiModel/baseApiResponseModel/ListBaseApiResponseModel";
import { BaseFilterListRequestModel } from "app/based/baseApiModel/baseFilterListModel/BaseFilterListRequestModel";
import { BaseFilterListResponseModel } from "app/based/baseApiModel/baseFilterListModel/BaseFilterListResponseModel";
import { BaseUserRequestModel } from "app/based/baseApiModel/baseUserModel/BaseUserRequestModel";
import { BaseUserResponseModel } from "app/based/baseApiModel/baseUserModel/BaseUserResponseModel";
import { FilterInjection } from "app/filters/di/FilterInjection";
import { FilterUseCase } from "app/filters/domain/useCase/FilterUseCase";
import { MobiSimRegistrationResponseModel } from "features/mobiSimRegistration/data/model/MobiSimRegistrationResponseModel";
import { MobiSimRegistrationInjection } from "features/mobiSimRegistration/di/MobiSimRegistrationInjection";
import { ExportPayload } from "features/mobiSimRegistration/domain/entities/MobiSimRegistrationPayload";
import {
  MobiSimRegistrationQuery,
  SearchContext,
  SearchFormValues,
} from "features/mobiSimRegistration/domain/entities/MobiSimRegistrationQuery";
import { MobiSimRegistrationRequestEntity } from "features/mobiSimRegistration/domain/entities/MobiSimRegistrationRequestEntity";
import { IMobiSimRegistrationRepository } from "features/mobiSimRegistration/domain/repositories/IMobiSimRegistrationRepository";
import { injectable } from "tsyringe";
import { productCache } from "utils/cache/productCache";
import { CacheKeyEnum } from "utils/enums/CacheKeyEnum";
import { CapabilityEnum } from "utils/enums/CapabilityEnum";
import { DateFormat } from "utils/format/DateFormat";
import { paramsChecking } from "utils/helpersInTs/helpersInTs";

@injectable()
export class MobiSimRegistrationUseCase {
  private mobiSimRegistrationRepo: IMobiSimRegistrationRepository;
  private filterUseCase: FilterUseCase;

  constructor() {
    this.mobiSimRegistrationRepo =
      MobiSimRegistrationInjection.getMobiSimRegistrationRepo();
    this.filterUseCase = FilterInjection.getUseCase();
  }

  public shouldUsePrivateTransactionList(): boolean {
    const cachedCapabilities = productCache?.getCache(
      CacheKeyEnum.capabilities
    );
    const hasPrivateTransactionList =
      Array.isArray(cachedCapabilities) &&
      cachedCapabilities.some(
        (item) =>
          (typeof item === "string" ? item : item?.key) ===
          CapabilityEnum.privateTransList
      );

    return hasPrivateTransactionList === true;
  }

  public shouldUsePrivateTransactionDetail(): boolean {
    const cachedCapabilities = productCache?.getCache(
      CacheKeyEnum.capabilities
    );
    const hasPrivateTransactionDetail =
      Array.isArray(cachedCapabilities) &&
      cachedCapabilities.some(
        (item) =>
          (typeof item === "string" ? item : item?.key) ===
          CapabilityEnum.privateTransDetail
      );

    return hasPrivateTransactionDetail === true;
  }

  private getListFromRepository(params: MobiSimRegistrationRequestEntity) {
    return this.mobiSimRegistrationRepo.getList(
      params,
      this.shouldUsePrivateTransactionList()
    );
  }

  private prepareFormValues(
    rawFormValues: Record<string, unknown>
  ): SearchFormValues {
    return {
      status: paramsChecking(rawFormValues?.status, "select") as
        | number
        | string,
      code: paramsChecking(rawFormValues?.code, "input") as string,
      transRef: paramsChecking(rawFormValues?.transRef, "input") as string,
      type: paramsChecking(rawFormValues?.type, "select") as number | string,
      platform: paramsChecking(rawFormValues?.platform, "select") as string,
      partner: paramsChecking(rawFormValues?.partner, "select") as string,
      user: (rawFormValues?.user as string[]) || undefined,
      store: paramsChecking(rawFormValues?.store, "select") as string,
    };
  }

  private getDateRange(rawFormValues: Record<string, unknown>): {
    dateFrom: string;
    dateTo: string;
  } {
    const dateFrom =
      DateFormat.dateRendering(
        rawFormValues?.created as Array<Record<string, unknown>>,
        "from"
      ) ?? "";
    const dateTo =
      DateFormat.dateRendering(
        rawFormValues?.created as Array<Record<string, unknown>>,
        "to"
      ) ?? "";
    return { dateFrom, dateTo };
  }

  async getList(
    params: MobiSimRegistrationRequestEntity
  ): Promise<ListBaseApiResponseModel<MobiSimRegistrationResponseModel>> {
    return await this.getListFromRepository(params);
  }

  async getMobiSimRegistrationListFromSearch(
    rawFormValues: Record<string, unknown>,
    context: SearchContext
  ): Promise<{
    list: ListBaseApiResponseModel<MobiSimRegistrationResponseModel>;
    params: MobiSimRegistrationRequestEntity;
  }> {
    const formValues = this.prepareFormValues(rawFormValues);
    const { dateFrom, dateTo } = this.getDateRange(rawFormValues);
    const params = MobiSimRegistrationQuery.buildSearchQuery(
      formValues,
      context,
      dateFrom,
      dateTo
    );
    const list = await this.getListFromRepository(params);
    return { list, params };
  }

  async getMobiSimRegistrationListWithTableChange(
    prevParams: MobiSimRegistrationRequestEntity,
    rawFormValues: Record<string, unknown>,
    page: number,
    limit: number
  ): Promise<{
    list: ListBaseApiResponseModel<MobiSimRegistrationResponseModel>;
    params: MobiSimRegistrationRequestEntity;
  }> {
    const formValues = this.prepareFormValues(rawFormValues);
    const { dateFrom, dateTo } = this.getDateRange(rawFormValues);
    const params = MobiSimRegistrationQuery.buildTableChangeQuery(
      prevParams,
      formValues,
      page,
      limit,
      dateFrom,
      dateTo
    );
    const list = await this.getListFromRepository(params);
    return { list, params };
  }

  async getMobiSimRegistrationListRedo(): Promise<{
    list: ListBaseApiResponseModel<MobiSimRegistrationResponseModel>;
    params: MobiSimRegistrationRequestEntity;
  }> {
    const { fromDate, toDate } = DateFormat.getDateRange(0);
    const params = MobiSimRegistrationQuery.buildRedoQuery(
      fromDate ?? "",
      toDate ?? ""
    );
    const list = await this.getListFromRepository(params);
    return { list, params };
  }

  async getDetail(
    params: MobiSimRegistrationRequestEntity
  ): Promise<BaseApiResponseModel<MobiSimRegistrationResponseModel>> {
    return await this.mobiSimRegistrationRepo.getDetail(
      params,
      this.shouldUsePrivateTransactionDetail()
    );
  }

  async exportReport(
    params: MobiSimRegistrationRequestEntity
  ): Promise<BaseApiResponseModel<Blob>> {
    return await this.mobiSimRegistrationRepo.exportReport(
      params,
      this.shouldUsePrivateTransactionList()
    );
  }

  async exportMobiSimRegistrationReportFromSearch(
    payload: ExportPayload
  ): Promise<BaseApiResponseModel<Blob>> {
    const formValues = this.prepareFormValues(payload.rawFormValues);
    const { dateFrom, dateTo } = this.getDateRange(payload.rawFormValues);
    const params = MobiSimRegistrationQuery.buildExportQuery(
      formValues,
      payload.context,
      dateFrom,
      dateTo
    );
    return await this.exportReport(params);
  }

  async checkStatus(
    code: string
  ): Promise<BaseApiResponseModel<MobiSimRegistrationResponseModel>> {
    return await this.mobiSimRegistrationRepo.checkStatus(code);
  }

  async getFilterList(
    params: BaseFilterListRequestModel,
    target: "carrier" | "partner" | "store" | "denomination" | "package"
  ): Promise<Array<BaseFilterListResponseModel>> {
    let path = "";
    switch (target) {
      case "carrier":
        path = CARRIER_MANAGEMENT.LIST;
        break;
      case "partner":
        path = PARTNER_MANAGEMENT.LIST;
        break;
      case "store":
        path = STORE_API.LIST;
        break;
      case "denomination":
        path = AIRTIME_MANAGEMENT.LIST;
        break;
      case "package":
        path = FILTER_FEATURE.PACKAGE_LIST;
        break;
    }
    return await this.filterUseCase.fetchListWithFeature({
      path,
      params,
      isNeedFeature: true,
    });
  }

  async getUserForFilter(
    params: BaseUserRequestModel,
    target: "staff" | "userB2B"
  ): Promise<Array<BaseUserResponseModel>> {
    let path = "";
    switch (target) {
      case "staff":
        path = FILTER_FEATURE.STAFF_LIST;
        break;
      case "userB2B":
        path = FILTER_FEATURE.USER_LIST_B2B;
        break;
    }
    return await this.filterUseCase.fetchListWithFeature({
      path,
      params,
      isNeedFeature: true,
    });
  }
}
